
import React, { useEffect } from 'react';
import Navigation from './Navigation';
import BreadcrumbNav from './BreadcrumbNav';
import UserMenu from './UserMenu';
import { useIsMobile } from '@/hooks/use-mobile';
import { applyTheme, getStoredTheme } from '@/utils/theme';
import OctovetoLogo from '@/components/ui/OctovetoLogo';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const isMobile = useIsMobile();

  useEffect(() => {
    const setVhProperty = () => {
      // Définir une propriété CSS personnalisée pour la hauteur réelle du viewport
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
      document.documentElement.style.setProperty('--mobile-vh', `${vh}px`);
    };

    const setupMobileOptimizations = () => {
      // Configuration pour les safe areas
      const root = document.documentElement;
      const body = document.body;
      
      // Forcer l'arrière-plan de l'application
      const bgColor = getComputedStyle(root).getPropertyValue('--background').trim();
      const fullBgColor = `hsl(${bgColor})`;
      
      root.style.backgroundColor = fullBgColor;
      body.style.backgroundColor = fullBgColor;
      
      // Ajouter la classe pour l'application
      body.classList.add('capacitor-app');
      
      // Gestion des métadonnées viewport pour mobile
      let viewport = document.querySelector('meta[name="viewport"]');
      if (!viewport) {
        viewport = document.createElement('meta');
        viewport.setAttribute('name', 'viewport');
        document.head.appendChild(viewport);
      }
      viewport.setAttribute('content',
        'width=device-width, initial-scale=1.0, viewport-fit=cover'
      );
    };

    // Configuration initiale
    // Applique le thème stocké (remplace le mode sombre si « futuriste »)
    applyTheme(getStoredTheme());
    setVhProperty();
    setupMobileOptimizations();

    // Gestion des événements de redimensionnement
    const handleResize = () => {
      setVhProperty();
    };

    const handleOrientationChange = () => {
      // Délai pour permettre au navigateur de recalculer
      setTimeout(() => {
        setVhProperty();
        setupMobileOptimizations();
      }, 100);
    };

    // Ajout des écouteurs d'événements
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleOrientationChange);
    
    // Détection du type d'appareil
    if (isMobile) {
      document.body.classList.add('mobile-device');
      
      // Détection spécifique iOS
      if (/iPhone|iPad|iPod/.test(navigator.userAgent)) {
        document.body.classList.add('ios-device');
        
        // Configuration spéciale pour iOS avec assertion de type pour les propriétés WebKit
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (document.body.style as any).webkitTextSizeAdjust = '100%';
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (document.body.style as any).webkitTouchCallout = 'none';
      }
      
      // Détection spécifique Android
      if (/Android/.test(navigator.userAgent)) {
        document.body.classList.add('android-device');
      }
    } else {
      document.body.classList.remove('mobile-device', 'ios-device', 'android-device');
    }

    // Nettoyage
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleOrientationChange);
    };
  }, [isMobile]);

  return (
    <div className="min-h-screen bg-background flex flex-col w-full max-w-full relative">
      {/* Header optimisé pour mobile */}
      <header className="flex-shrink-0 border-b border-white/8 bg-card/95 backdrop-blur-md shadow-soft w-full sticky top-0 z-40">
        <div className="w-full px-3 sm:px-4 py-2 sm:py-3 flex justify-between items-center max-w-full">
          <div className="flex items-center min-w-0 flex-1 mr-2 sm:mr-4">
            <OctovetoLogo size="sm" />
          </div>
          <div className="flex-shrink-0">
            <UserMenu />
          </div>
        </div>
      </header>

      {/* Contenu principal avec scroll sécurisé */}
      <main className="flex-1 w-full">
        <div className="w-full px-3 sm:px-4 pt-3 sm:pt-4 pb-[calc(env(safe-area-inset-bottom)+5rem)] max-w-full">
          <BreadcrumbNav />
          <div className="space-y-3 sm:space-y-4 w-full max-w-full">
            {children}
          </div>
        </div>
      </main>

      {/* Navigation fixe en bas (gérée par le composant) */}
      <Navigation />
    </div>
  );
};

export default Layout;
